import { useEffect, useState } from 'react';

import { AxiosError } from 'axios';

import { apiStrapi } from 'services/apiService';
import { logger } from 'utils/logger';
import { formatPhoneDisplay } from 'utils/validators/phone';

interface SupportContactResponse {
  phone?: string;
  text?: string;
}

// Simple in-memory cache
let cachedWhatsappNumber: string | null | undefined = undefined;
let cachedWhatsappText: string | null | undefined = undefined;
let cachedError: Error | null = null;
let cachedStatus: 'idle' | 'loading' | 'done' = 'idle';

export const useDynamicWhatsapp = () => {
  const [whatsappNumber, setWhatsappNumber] = useState<string | null>(cachedWhatsappNumber ?? null);
  const [whatsappText, setWhatsappText] = useState<string | null>(cachedWhatsappText ?? null);
  const [isLoading, setIsLoading] = useState(cachedStatus === 'loading');
  const [error, setError] = useState<Error | null>(cachedError);

  useEffect(() => {
    let isMounted = true;
    if (cachedStatus === 'done') {
      return;
    }
    if (cachedStatus === 'loading') {
      return;
    }

    const loadWhatsappData = async () => {
      cachedStatus = 'loading';
      setIsLoading(true);
      try {
        const response = await apiStrapi.get<SupportContactResponse>(
          '/organizations/support-contact'
        );
        const data = response.data;

        if (data && data.phone) {
          const formattedNumber = formatPhoneDisplay(data.phone);
          cachedWhatsappNumber = formattedNumber;
          cachedWhatsappText = data.text || '';
          cachedStatus = 'done';

          if (isMounted) {
            setWhatsappNumber(formattedNumber);
            setWhatsappText(data.text || '');
          }
          return;
        } else {
          logger.warn(
            '[useDynamicWhatsapp] WhatsApp data not found or unexpected format:',
            response.data
          );
          cachedWhatsappNumber = null;
          cachedWhatsappText = null;
          cachedStatus = 'done';

          if (isMounted) {
            setWhatsappNumber(null);
            setWhatsappText(null);
          }
        }
      } catch (apiError) {
        logger.error('[useDynamicWhatsapp] Error loading WhatsApp data:', apiError);
        const axiosError = apiError as AxiosError;
        if (axiosError.response?.status === 404) {
          cachedWhatsappNumber = null;
          cachedWhatsappText = null;
          cachedStatus = 'done';

          if (isMounted) {
            setWhatsappNumber(null);
            setWhatsappText(null);
          }
        } else {
          cachedError = apiError as Error;
          cachedStatus = 'done';
          if (isMounted) setError(apiError as Error);
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadWhatsappData();
    return () => {
      isMounted = false;
    };
  }, []);

  return { whatsappNumber, whatsappText, isLoading, error };
};
